import React, { ReactNode } from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { StatusInfoLoading } from '../../components/StatusInfo/StatusInfoLoading';
import { StatusInfoPositiveCheck } from '../../components/StatusInfo/StatusInfoPositiveCheck';
import { StatusInfoError, StatusInfoErrorWithRequestId } from '../../components/StatusInfo';
import LiveRegion from '../../../components/atoms/LiveRegion/LiveRegion';
import { getRequestIdFromHeaders } from '../../../lib/HeaderUtils/RequestID';
import { SonResponseError, SonResponseErrorTypes } from '../types/SonResponseError';
import { FetchState } from '../../../hooks/reducers/useFetchReducer';
import { getDefaultErrorTypeTranslations } from '../utils/defaultErrorTypeTranslations';
import { translatePartOfStringWithLink } from '../../../lib/StringUtils/StringHTMLUtils';
import { useAppContext } from '../../../contexts/AppContext/AppContext';

export type DefaultStatusMessages = {
  messageSuccess: ReactNode;
  messageForward?: string | undefined;
  messageSending: ReactNode;
  messageFail: ReactNode;
};

/**
 * Custom Error Type Message for overriding errorTypes for specific forms.
 */
export type CustomErrorTypeMessages = {
  customErrorTypeMessages?: Partial<Record<SonResponseErrorTypes, string>>;
};

export type StatusMessages<T> = DefaultStatusMessages &
  CustomErrorTypeMessages & {
    closePreventedMessage?: string | null;
    customErrorResponseMapper?: (errorResponse: AxiosResponse<any>) => ReactNode | undefined;
    fetchState: FetchState<AxiosResponse<T>, AxiosError<SonResponseError>>;
  };

export function StatusMessages<T extends { url?: string }>({
  messageSuccess,
  messageForward,
  messageSending,
  messageFail,
  customErrorTypeMessages,
  fetchState,
  customErrorResponseMapper,
  closePreventedMessage,
}: StatusMessages<T>) {
  const { t } = useTranslation();
  const { portalConfiguration } = useAppContext();

  /**
   * Get custom messages for specific error types.
   */
  const getCustomDisplayableErrorMessage = (type: SonResponseErrorTypes): string | undefined => {
    return customErrorTypeMessages ? customErrorTypeMessages[type] : undefined;
  };

  const getFailMessage = (err: AxiosError<SonResponseError>): ReactNode => {
    const errorType = err?.response?.data?.errorType;

    if (errorType) {
      return (
        getCustomDisplayableErrorMessage(errorType) ||
        getDefaultErrorTypeTranslations(t, err?.response?.data, portalConfiguration)
      );
    }

    return messageFail;
  };

  // for easier testing we provide every status with a data-form-status attribute
  const liveRegionMessage = () => {
    switch (fetchState.type) {
      case 'loading':
        return (
          <StatusInfoLoading modifier={['padding-top', 'right']} message={messageSending} dataFormStatus="sending" />
        );
      case 'success':
        return (
          <>
            <StatusInfoPositiveCheck
              modifier={['padding-top', 'right']}
              message={messageSuccess}
              dataFormStatus="success"
            />
            {messageForward && fetchState.response?.data?.url
              ? (
                  <StatusInfoLoading
                    modifier={['padding-top', 'right']}
                    // Use window location (withRouting=false) to ensure full page reload, closing the modal
                    // This matches the automatic redirect behavior in useRedirect hook
                    message={translatePartOfStringWithLink(messageForward, fetchState.response?.data?.url, false)}
                    dataFormStatus="forwarding"
                  />
                )
              : null}
          </>
        );
      case 'error': {
        const requestError = fetchState.requestError;
        const isClientError =
          requestError.status &&
          requestError.status >= 400 &&
          requestError.status < 500;

        if (isClientError) {
          return (
            <StatusInfoError
              modifier={['padding-top', 'red', 'right']}
              message={getFailMessage(requestError)}
              additionalContent={customErrorResponseMapper?.(requestError?.response as any)}
              dataFormStatus="fail"
            />
          );
        } else {
          return (
            <StatusInfoErrorWithRequestId
              modifier={['padding-top', 'red', 'right']}
              role="alert"
              message={getFailMessage(requestError)}
              additionalContent={customErrorResponseMapper?.(requestError?.response as any)}
              requestId={getRequestIdFromHeaders(requestError?.response?.headers)}
              dataFormStatus="fail"
            />
          );
        }
      }
      default:
        return null;
    }
  };

  return (
    <LiveRegion htmlTag="div" invisible={false} ariaAtomic>
      {liveRegionMessage()}
      {closePreventedMessage && <StatusInfoError message={closePreventedMessage} modifier="right" />}
    </LiveRegion>
  );
}
